/**
 * File Validation Utilities
 * 
 * Centralized file validation for uploads across all platforms
 * Implements production-ready validation with clear error messages
 * 
 * @author AI Assistant
 * @created 2024
 */

// Platform-specific file size limits (in bytes)
export const FILE_SIZE_LIMITS = {
  // Image limits
  images: {
    default: 5 * 1024 * 1024, // 5MB default
    instagram: 8 * 1024 * 1024, // 8MB for Instagram
    facebook: 10 * 1024 * 1024, // 10MB for Facebook
    twitter: 5 * 1024 * 1024, // 5MB for Twitter
    linkedin: 8 * 1024 * 1024, // 8MB for LinkedIn
    tiktok: 50 * 1024 * 1024, // 50MB for TikTok images
    threads: 8 * 1024 * 1024, // 8MB for Threads
    bluesky: 5 * 1024 * 1024, // 5MB for BlueSky
    mastodon: 8 * 1024 * 1024, // 8MB for Mastodon
  },
  // Video limits
  videos: {
    default: 100 * 1024 * 1024, // 100MB default
    instagram: 250 * 1024 * 1024, // 250MB for Instagram
    facebook: 500 * 1024 * 1024, // 500MB for Facebook
    twitter: 512 * 1024 * 1024, // 512MB for Twitter
    linkedin: 200 * 1024 * 1024, // 200MB for LinkedIn
    tiktok: 500 * 1024 * 1024, // 500MB for TikTok
    threads: 100 * 1024 * 1024, // 100MB for Threads
    bluesky: 50 * 1024 * 1024, // 50MB for BlueSky
    mastodon: 100 * 1024 * 1024, // 100MB for Mastodon
  }
};

// Supported file formats
export const SUPPORTED_FORMATS = {
  images: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  videos: ['video/mp4', 'video/quicktime', 'video/avi', 'video/mov'],
  // Platform-specific format restrictions
  platformRestrictions: {
    tiktok: {
      images: ['image/jpeg', 'image/png', 'image/gif'],
      videos: ['video/mp4']
    },
    instagram: {
      images: ['image/jpeg', 'image/png'],
      videos: ['video/mp4']
    },
    twitter: {
      images: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      videos: ['video/mp4']
    }
  }
};

/**
 * Format file size for human-readable display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "5.2 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Get maximum file size for a file type and platforms
 * @param {string} fileType - File MIME type
 * @param {Array<string>} platforms - Array of platform names
 * @returns {number} Maximum allowed file size in bytes
 */
export const getMaxFileSize = (fileType, platforms = []) => {
  const isVideo = fileType.startsWith('video/');
  const categoryLimits = isVideo ? FILE_SIZE_LIMITS.videos : FILE_SIZE_LIMITS.images;
  
  if (!platforms || platforms.length === 0) {
    return categoryLimits.default;
  }
  
  // Find the most restrictive limit among selected platforms
  const platformLimits = platforms.map(platform => {
    const normalizedPlatform = platform.toLowerCase();
    return categoryLimits[normalizedPlatform] || categoryLimits.default;
  });
  
  return Math.min(...platformLimits);
};

/**
 * Get supported formats for platforms
 * @param {Array<string>} platforms - Array of platform names
 * @returns {object} Object with images and videos arrays of supported formats
 */
export const getSupportedFormats = (platforms = []) => {
  if (!platforms || platforms.length === 0) {
    return {
      images: SUPPORTED_FORMATS.images,
      videos: SUPPORTED_FORMATS.videos
    };
  }
  
  // Find intersection of supported formats across platforms
  let supportedImages = [...SUPPORTED_FORMATS.images];
  let supportedVideos = [...SUPPORTED_FORMATS.videos];
  
  platforms.forEach(platform => {
    const normalizedPlatform = platform.toLowerCase();
    const restrictions = SUPPORTED_FORMATS.platformRestrictions[normalizedPlatform];
    
    if (restrictions) {
      supportedImages = supportedImages.filter(format => 
        restrictions.images.includes(format)
      );
      supportedVideos = supportedVideos.filter(format => 
        restrictions.videos.includes(format)
      );
    }
  });
  
  return {
    images: supportedImages,
    videos: supportedVideos
  };
};

/**
 * Validate a file against platform requirements
 * @param {File} file - File to validate
 * @param {Array<string>} platforms - Array of platform names
 * @param {object} options - Additional validation options
 * @returns {object} Validation result with isValid, errors, and warnings
 */
export const validateFile = (file, platforms = [], options = {}) => {
  const result = {
    isValid: true,
    errors: [],
    warnings: [],
    fileSize: file.size,
    formattedSize: formatFileSize(file.size),
    maxAllowedSize: 0,
    formattedMaxSize: '',
    fileType: file.type,
    fileName: file.name
  };
  
  // Check if file exists
  if (!file) {
    result.isValid = false;
    result.errors.push('No file selected');
    return result;
  }
  
  // Get platform requirements
  const maxSize = getMaxFileSize(file.type, platforms);
  const supportedFormats = getSupportedFormats(platforms);
  
  result.maxAllowedSize = maxSize;
  result.formattedMaxSize = formatFileSize(maxSize);
  
  // Validate file type
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  
  if (!isImage && !isVideo) {
    result.isValid = false;
    result.errors.push(`Unsupported file type: ${file.type}. Please upload an image or video file.`);
    return result;
  }
  
  // Validate format against platform restrictions
  const allowedFormats = isImage ? supportedFormats.images : supportedFormats.videos;
  
  if (!allowedFormats.includes(file.type)) {
    result.isValid = false;
    const formatType = isImage ? 'image' : 'video';
    const platformText = platforms.length > 0 ? ` for ${platforms.join(', ')}` : '';
    result.errors.push(
      `${file.type} format not supported${platformText}. ` +
      `Supported ${formatType} formats: ${allowedFormats.join(', ')}`
    );
  }
  
  // Validate file size
  if (file.size > maxSize) {
    result.isValid = false;
    const platformText = platforms.length > 0 ? ` for ${platforms.join(', ')}` : '';
    result.errors.push(
      `File size (${result.formattedSize}) exceeds maximum allowed size ` +
      `(${result.formattedMaxSize})${platformText}`
    );
  }
  
  // Add warnings for large files (>80% of limit)
  const warningThreshold = maxSize * 0.8;
  if (file.size > warningThreshold && file.size <= maxSize) {
    result.warnings.push(
      `Large file size (${result.formattedSize}). Consider optimizing for faster upload.`
    );
  }
  
  // Additional validations
  if (options.maxDimensions && isImage) {
    // Note: Image dimension validation would require loading the image
    // This can be implemented as a separate async function if needed
    result.warnings.push('Image dimension validation not performed');
  }
  
  return result;
};

/**
 * Validate multiple files (for batch uploads)
 * @param {FileList|Array<File>} files - Files to validate
 * @param {Array<string>} platforms - Array of platform names
 * @param {object} options - Additional validation options
 * @returns {object} Validation result for all files
 */
export const validateFiles = (files, platforms = [], options = {}) => {
  const fileArray = Array.from(files);
  const results = fileArray.map(file => validateFile(file, platforms, options));
  
  const allValid = results.every(result => result.isValid);
  const allErrors = results.flatMap(result => result.errors);
  const allWarnings = results.flatMap(result => result.warnings);
  const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);
  
  return {
    isValid: allValid,
    errors: allErrors,
    warnings: allWarnings,
    fileCount: fileArray.length,
    totalSize,
    formattedTotalSize: formatFileSize(totalSize),
    individualResults: results
  };
};

/**
 * Get file validation summary for UI display
 * @param {object} validation - Validation result from validateFile
 * @returns {object} Summary object for UI components
 */
export const getValidationSummary = (validation) => {
  return {
    status: validation.isValid ? 'success' : 'error',
    hasWarnings: validation.warnings.length > 0,
    primaryMessage: validation.isValid 
      ? `File ready for upload (${validation.formattedSize})`
      : validation.errors[0],
    allMessages: [...validation.errors, ...validation.warnings],
    color: validation.isValid 
      ? (validation.warnings.length > 0 ? 'warning' : 'success')
      : 'error'
  };
};

/**
 * Create file validation error message for specific platform
 * @param {string} platform - Platform name
 * @param {string} fileType - File type (image/video)
 * @returns {string} Detailed error message with platform limits
 */
export const getPlatformLimitMessage = (platform, fileType = 'image') => {
  const normalizedPlatform = platform.toLowerCase();
  const isVideo = fileType === 'video' || fileType.startsWith('video/');
  const categoryLimits = isVideo ? FILE_SIZE_LIMITS.videos : FILE_SIZE_LIMITS.images;
  const limit = categoryLimits[normalizedPlatform] || categoryLimits.default;
  
  return `${platform} ${isVideo ? 'video' : 'image'} limit: ${formatFileSize(limit)}`;
};

/**
 * Get HTML accept attribute string for file input based on selected platforms
 * @param {Array<string>} platforms - Array of platform names
 * @returns {string} Accept attribute string for file input
 */
export const getFileAcceptString = (platforms = []) => {
  const supportedFormats = getSupportedFormats(platforms);
  
  // Combine image and video formats
  const allFormats = [
    ...supportedFormats.images,
    ...supportedFormats.videos
  ];
  
  // If no platforms selected, use default formats
  if (allFormats.length === 0) {
    return 'image/*,video/mp4';
  }
  
  return allFormats.join(',');
};

export default {
  validateFile,
  validateFiles,
  formatFileSize,
  getMaxFileSize,
  getSupportedFormats,
  getValidationSummary,
  getPlatformLimitMessage,
  getFileAcceptString,
  FILE_SIZE_LIMITS,
  SUPPORTED_FORMATS
}; 